import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Globe, Check } from 'lucide-react'
import { getAllNetworks } from '@/config/networks'
import { storageService } from '@/services/wallet/storage.service'

import { useWalletStore } from '@/store/walletStore'

const NetworkSelector: React.FC = () => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const { selectedNetwork, setSelectedNetwork } = useWalletStore()
  const dropdownRef = useRef<HTMLDivElement>(null)

  const visibleNetworks = getAllNetworks().filter(
    (network) => network.id === 'omdb' || network.id === 'bsc' || network.isCustom
  )

  const currentNetwork =
    visibleNetworks.find((n) => n.id === selectedNetwork) || visibleNetworks[0]

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (networkId: string) => {
    storageService.saveSelectedNetwork(networkId)
    setSelectedNetwork(networkId)
    setIsOpen(false)

    // Disparar evento para componentes que no usan el store
    document.dispatchEvent(
      new CustomEvent('network:changed', { detail: { networkId } })
    )
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 lg:py-2.5 rounded-full bg-card border border-border-base transition-all duration-200 group shadow-sm"
      >
        <div className="w-5 h-5 rounded-full overflow-hidden bg-primary/20 flex items-center justify-center">
          {currentNetwork.logoUrl ? (
            <img
              src={currentNetwork.logoUrl}
              alt={currentNetwork.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <Globe className="w-3 h-3 text-primary" />
          )}
        </div>
        <span className="text-sm font-medium text-text-main hidden sm:block">
          {currentNetwork.name}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-text-secondary transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 place-self-end w-56 rounded-xl bg-card border border-border-base shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="p-2 space-y-1">
            <div className="px-2 py-1.5 text-xs font-semibold text-text-secondary uppercase tracking-wider">
              {t('network.select_network')}
            </div>
            {visibleNetworks.map((network) => (
              <button
                key={network.id}
                onClick={() => handleSelect(network.id)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedNetwork === network.id
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-text-main hover:bg-card-hover'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full overflow-hidden bg-primary/20 flex items-center justify-center shrink-0">
                    {network.logoUrl ? (
                      <img
                        src={network.logoUrl}
                        alt={network.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Globe className="w-3.5 h-3.5 text-primary" />
                    )}
                  </div>
                  <div className="flex flex-col items-start">
                    <span>{network.name}</span>
                    {network.isTestnet && (
                      <span className="text-[10px] px-1 rounded bg-orange-500/20 text-orange-500 border border-orange-500/30">
                        Testnet
                      </span>
                    )}
                  </div>
                </div>
                {selectedNetwork === network.id && (
                  <Check className="w-4 h-4" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default NetworkSelector
